import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { trackAction } from "./PageAnalytics";
import ProjectGallery from "./ProjectGallery";
import ShowcaseVisual from "./ShowcaseVisual";

// One selected project: the gallery, then one caption cluster — the name and
// one paragraph on the work and who owned what, a labelled outcome that keeps
// its qualification beside it, and the way into the article. The stage opens
// the screens in the media dialog; "Read case study" is the way into the
// article, named after the article's own title.

export const projectAnchor = (id) => `project-${id}`;

export { ShowcaseVisual };

// The shared secondary action: a quiet filled pill on a native link.
export function PillLink({ to, children, onClick, className = "" }) {
  return (
    <Link to={to} className={`pf-pill ${className}`.trim()} onClick={onClick}>
      {children}
      <ArrowRight size={16} strokeWidth={1.75} aria-hidden="true" />
    </Link>
  );
}

// The same pill for a page on another site. It opens in a new tab and says
// so; `name` completes the accessible name when the label alone is generic.
export function ExternalPillLink({ href, children, name, onClick, className = "" }) {
  return (
    <a
      href={href}
      className={`pf-pill pf-pill--external ${className}`.trim()}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
    >
      <span>
        {children}
        <span className="pf-visually-hidden">
          {name ? `: ${name}` : ""} (opens in a new tab)
        </span>
      </span>
      <ArrowUpRight size={16} strokeWidth={1.75} aria-hidden="true" />
    </a>
  );
}

export function CaseStudyLink({ project, label = "Read case study" }) {
  return (
    <PillLink
      to={project.href}
      onClick={() => trackAction("open_case_study", `${project.id}:link`)}
    >
      <span>
        {label}
        <span className="pf-visually-hidden">: {project.title}</span>
      </span>
    </PillLink>
  );
}

// The result, labelled as one, with the number and its meaning in one
// sentence and the qualification straight after it.
export function Outcome({ outcome, className = "" }) {
  return (
    <div className={`pf-outcome ${className}`.trim()}>
      <p className="pf-outcome__label">Outcome</p>
      <p className="pf-outcome__text">
        {outcome.value && (
          <>
            <span className="pf-outcome__value">{outcome.value}</span>{" "}
          </>
        )}
        {outcome.text}
      </p>
    </div>
  );
}

// Name and summary on the left, the outcome on the right behind its rule,
// and the action under the left column. One column on narrower screens, in
// the same reading order.
export function ProjectCaption({ project, headingId, className = "" }) {
  return (
    <div className={`pf-show__caption ${className}`.trim()}>
      <div className="pf-show__lede">
        <h3 id={headingId} className="pf-show__title">
          {project.title}
        </h3>
        <p className="pf-show__summary">{project.summary}</p>
      </div>
      <Outcome outcome={project.outcome} className="pf-show__outcome" />
      <div className="pf-show__action">
        <CaseStudyLink project={project} />
      </div>
    </div>
  );
}

function ProjectShowcase({ project, priority = false }) {
  const headingId = `showcase-${project.id}`;

  return (
    <article
      id={projectAnchor(project.id)}
      tabIndex={-1}
      className={`pf-show pf-show--${project.id}`}
      aria-labelledby={headingId}
    >
      {project.gallery ? (
        <ProjectGallery project={project} priority={priority} />
      ) : (
        <ShowcaseVisual project={project} priority={priority} />
      )}
      <ProjectCaption project={project} headingId={headingId} />
    </article>
  );
}

export default ProjectShowcase;
